import gsap from "gsap";
import { BreakpointListener, clear } from "./utils";
import ScrollScene from "./scroll-scene";
import ScrollController from "./scroll-controller";
import AnimationCreator from "./creator/animation.creator";
import TypeHelper from "./helpers/type.helper";
import Parser from "./parser";
import AnimationParser from "./parsers/animation.parser";
import SceneParser from "./parsers/scene.parser";
import ParallaxParser from "./parsers/parallax.parser";
import AnimationBuilder from "./builders/animation.builder";
import SceneBuilder from "./builders/scene.builder";
import ParallaxBuilder from "./builders/parallax.builder";
var ScrollXP = /** @class */ (function () {
    function ScrollXP(options) {
        var _this = this;
        this.defaults = {};
        this.registeredScenes = {};
        this.registeredAnimations = {};
        this.domElements = [];
        this.tweens = [];
        this.scenes = [];
        this.anchorScenes = [];
        gsap.config({
            nullTargetWarn: false,
        });
        this.container = options.container || window;
        this.content =
            this.container instanceof Window
                ? document.body.children[0]
                : this.container.children[0];
        this.scrollOffset = options.scrollOffset || 0;
        this._smoothScrolling = options.smoothScrolling || false;
        this.parser = new Parser(options.breakpoints);
        // TODO: Instead of checking for xs, check for isMobile somehow (actual devices)
        new BreakpointListener(function (result) {
            if (result.hasChanged) {
                // First load
                if (!_this.controller) {
                    _this.controller = new ScrollController({
                        container: _this.container,
                        smoothScrolling: _this.smoothScrolling,
                        addIndicators: options.addIndicators || false,
                    });
                }
                else {
                    // Disable smooth scrolling on mobile
                    if (_this.smoothScrolling) {
                        if (result.screenSize === "xs") {
                            _this.controller.smoothScrolling = false;
                        }
                        else {
                            _this.controller.smoothScrolling = true;
                        }
                    }
                }
                console.debug("[ScrollView] Rebuiling scenes for:", result.screenSize);
                _this.rebuild();
            }
            // Update content scene duration, so the scroll progress is adjusted
            // if (this.contentScene) {
            //   this.contentScene.duration(this.content.offsetHeight)
            // }
        }, this.parser.breakpoints);
        // Set up scrollTo anchors
        if (options.anchors && options.anchors.length > 0) {
            var anchors = Array.from(options.anchors).filter(function (anchor) { return anchor.hash.length > 1; });
            this.setUpScrollTo(anchors);
        }
    }
    ScrollXP.prototype.setUpScrollTo = function (anchors) {
        var _this = this;
        anchors.forEach(function (anchor) {
            anchor.addEventListener("click", function (e) {
                anchors.forEach(function (anchor) { return anchor.classList.remove("is-active"); });
                if (e.currentTarget) {
                    var currentTarget = e.currentTarget;
                    currentTarget.classList.add("is-active");
                    var id = currentTarget.getAttribute("href");
                    if (id && id.length > 0) {
                        e.preventDefault();
                        _this.controller.scrollTo(id);
                    }
                }
            });
        });
    };
    ScrollXP.prototype.bindAnchors = function (anchors) {
        var _this = this;
        this.setUpScrollTo(anchors);
        // Bind anchor to scroll scenes
        anchors.forEach(function (anchor) {
            var id = anchor.getAttribute("href");
            if (id) {
                var section = _this.container instanceof Window ? document.body.querySelector(id) : _this.container.querySelector(id);
                if (section) {
                    var elem_1 = section;
                    var anchorScene = new ScrollScene({
                        triggerElement: elem_1,
                        triggerHook: "0.01",
                        offset: _this.scrollOffset,
                        duration: function () {
                            return elem_1.offsetHeight;
                        },
                    })
                        .on("enter", function () { return anchor.classList.add("is-active"); })
                        .on("leave", function () { return anchor.classList.remove("is-active"); });
                    _this.anchorScenes.push(anchorScene);
                    _this.controller.addScene(anchorScene);
                }
            }
        });
    };
    Object.defineProperty(ScrollXP.prototype, "smoothScrolling", {
        get: function () {
            return this._smoothScrolling;
        },
        set: function (newValue) {
            this.controller.smoothScrolling = newValue;
            this.rebuild();
        },
        enumerable: false,
        configurable: true
    });
    ScrollXP.prototype.setScrollOffset = function (offset) {
        this.anchorScenes.forEach(function (scene) { return scene.offset(offset); });
        this.controller.setScrollOffset(offset);
    };
    ScrollXP.prototype.register = function (data) {
        var _this = this;
        if (TypeHelper.isSceneDescriptor(data)) {
            if (data.name === undefined) {
                throw new Error("Scene is missing name, it wasn't possible to register it.");
            }
            this.registeredScenes[data.name] = clear(data, "name");
        }
        else if (TypeHelper.isAnimationDescriptor(data)) {
            if (data.name === undefined) {
                throw new Error("Animation is missing name, it wasn't possible to register it.");
            }
            if (data.label !== undefined) {
                throw new Error("Animations can't be registered with a label, it can only be set using data-* attributes. Please, review \"" + data.name + "\".");
            }
            this.registeredAnimations[data.name] = clear(data, "name");
        }
        else if (data instanceof Array) {
            data.forEach(function (item) { return _this.register(item); });
        }
    };
    ScrollXP.prototype.setDefault = function (descriptor) {
        if (TypeHelper.isAnimationDescriptor(descriptor)) {
            this.defaults.animation = descriptor;
        }
        else if (TypeHelper.isSceneDescriptor(descriptor)) {
            this.defaults.scene = descriptor;
        }
        else if (TypeHelper.isParallaxDescriptor(descriptor)) {
            this.defaults.parallax = descriptor;
        }
        else {
            throw new TypeError("Default value is not a valid descriptor: " + descriptor);
        }
    };
    ScrollXP.prototype.getRegisteredScenes = function () {
        return this.registeredScenes;
    };
    ScrollXP.prototype.getRegisteredAnimations = function () {
        return this.registeredAnimations;
    };
    ScrollXP.prototype.rebuild = function () {
        var _this = this;
        setTimeout(function () {
            _this.resetScenes();
            _this.buildParallaxScenes();
            _this.buildScenes();
        }, 0);
    };
    ScrollXP.prototype.buildScenes = function () {
        var _this = this;
        var parser = this.parser.create(SceneParser, this.defaults.scene);
        var container = this.container instanceof Window ? document.body : this.container;
        var elements = parser.getElements(container);
        elements.forEach(function (element) {
            var descriptor;
            // Is registered scene?
            var name = parser.getName(element);
            if (name) {
                // Creates a new parser, so the result is the following composition of descriptors:
                // Default <- Registered <- Parsed
                if (name in _this.registeredScenes) {
                    var newParser = _this.parser.create(SceneParser, _this.registeredScenes[name]);
                    descriptor = newParser.parse(element, container);
                }
                else {
                    throw new Error("Couldn't find scene \"" + name + "\". Make sure it's registered.");
                }
            }
            else {
                // Parses the element, so the result is the following composition of descriptors:
                // Default <- Parsed
                descriptor = parser.parse(element, container);
            }
            if (descriptor.enabled) {
                var scene_1 = new ScrollScene({
                    triggerElement: descriptor.trigger || element,
                    triggerHook: descriptor.hook,
                    duration: descriptor.duration,
                    reverse: descriptor.reverse,
                });
                // Add indicators?
                if (descriptor.indicator) {
                    scene_1.addIndicators({ name: descriptor.indicator });
                }
                // onEnter callback
                if (descriptor.onEnter) {
                    scene_1.on("enter", function (vars) {
                        if (descriptor.onEnter) {
                            descriptor.onEnter(element, scene_1, vars);
                        }
                    });
                }
                // onLeave callback
                if (descriptor.onLeave) {
                    scene_1.on("leave", function (vars) {
                        if (descriptor.onLeave) {
                            descriptor.onLeave(element, scene_1, vars);
                        }
                    });
                }
                // onProgress callback
                if (descriptor.onProgress) {
                    scene_1.on("progress", function (vars) {
                        if (descriptor.onProgress) {
                            descriptor.onProgress(element, scene_1, vars);
                        }
                    });
                }
                // 1. Has class toggle?
                if (descriptor.classToggle) {
                    scene_1.setClassToggle(element, descriptor.classToggle);
                }
                // 2. Is pinned?
                else if (descriptor.pin) {
                    scene_1.setPin(element);
                }
                // 3. Parse animations
                else {
                    _this.createAnimation(scene_1, element);
                }
                _this.controller.addScene(scene_1);
                _this.scenes.push(scene_1);
            }
        });
    };
    ScrollXP.prototype.createAnimation = function (scene, domScene) {
        var _this = this;
        var creator = new AnimationCreator();
        var parser = this.parser.create(AnimationParser, this.defaults.animation);
        var easing = false;
        var elements = parser.getElements(domScene);
        elements.forEach(function (element) {
            var descriptor;
            // Is registered animation?
            var name = parser.getName(element);
            if (name) {
                // Creates a new parser, so the result is the following composition of descriptors:
                // Default <- Registered <- Parsed
                if (name in _this.registeredAnimations) {
                    var newParser = _this.parser.create(AnimationParser, _this.registeredAnimations[name]);
                    descriptor = newParser.parse(element);
                }
                else {
                    throw new Error("Couldn't find animation \"" + name + "\". Make sure it's registered.");
                }
            }
            else {
                // Parses the element, so the result is the following composition of descriptors:
                // Default <- Parsed
                descriptor = parser.parse(element);
            }
            // Sets flag to render scene properly
            if (descriptor.momentum > 0) {
                easing = true;
            }
            // Adds the animated elements to the elements array
            if (descriptor.stagger) {
                for (var i = 0; i < element.children.length; i++) {
                    _this.domElements.push(element.children[i]);
                }
            }
            else {
                _this.domElements.push(element);
            }
            creator.add(element, descriptor);
        });
        var tween = creator.create();
        scene.setTween(tween, easing);
        this.tweens.push(tween);
    };
    ScrollXP.prototype.buildParallaxScenes = function () {
        var _this = this;
        var parser = this.parser.create(ParallaxParser, this.defaults.parallax);
        var container = this.container instanceof Window ? document.body : this.container;
        var elements = parser.getElements(container);
        var globalDescriptors = [];
        elements.forEach(function (element) {
            var descriptor = parser.parse(element, container);
            if (descriptor.enabled) {
                // Global parallax
                if (descriptor.type === "global") {
                    globalDescriptors.push(descriptor);
                    if (globalDescriptors.length === 1) {
                        var scene = new ScrollScene({
                            triggerElement: _this.content,
                            triggerHook: "onLeave",
                            duration: _this.content.offsetHeight,
                        }).on("update", function () {
                            _this.updateParallaxElements(globalDescriptors, _this.controller.getScrollPos());
                        });
                        _this.scenes.push(scene);
                        _this.controller.addScene(scene);
                    }
                }
                // Scene parallax
                else {
                    var during_1 = false;
                    var scene = new ScrollScene({
                        triggerElement: descriptor.trigger,
                        triggerHook: descriptor.hook,
                        duration: descriptor.duration,
                        offset: descriptor.offset,
                    })
                        .on("update", function (e) {
                        var _a, _b;
                        if (during_1 && ((_a = e === null || e === void 0 ? void 0 : e.vars) === null || _a === void 0 ? void 0 : _a.scrollPos) && ((_b = e === null || e === void 0 ? void 0 : e.vars) === null || _b === void 0 ? void 0 : _b.startPos)) {
                            var delta = e.vars.scrollPos - e.vars.startPos;
                            _this.updateParallaxElements(descriptor, delta);
                        }
                    })
                        .on("enter", function () { return (during_1 = true); })
                        .on("leave", function () { return (during_1 = false); });
                    if (descriptor.indicator) {
                        scene.addIndicators({ name: descriptor.indicator });
                    }
                    _this.scenes.push(scene);
                    _this.controller.addScene(scene);
                }
            }
            else {
                // In the case the parallax was disabled for this element, clear all styles
                gsap.set(element, { clearProps: "all" });
            }
        });
    };
    ScrollXP.prototype.updateParallaxElements = function (data, offsetY) {
        var _this = this;
        if (data instanceof Array) {
            data.forEach(function (descriptor) { return _this.updateParallaxElements(descriptor, offsetY); });
        }
        else if (data.element) {
            if (data.stagger) {
                gsap.to(data.element.children, {
                    duration: data.momentum,
                    y: offsetY / data.speed,
                    stagger: {
                        ease: data.ease,
                        each: data.stagger,
                    },
                });
            }
            else {
                if (data.momentum > 0) {
                    gsap.to(data.element, data.momentum, {
                        y: offsetY / data.speed,
                        ease: data.ease,
                    });
                }
                else {
                    gsap.set(data.element, {
                        y: offsetY / data.speed,
                    });
                }
            }
        }
    };
    ScrollXP.prototype.resetScenes = function () {
        var _this = this;
        this.tweens.forEach(function (tween) { return tween.clear(); });
        this.scenes.forEach(function (scene) {
            scene.removePin(true);
            _this.controller.removeScene(scene);
        });
        gsap.set(this.domElements, {
            clearProps: "all",
        });
        this.tweens = [];
        this.scenes = [];
        this.domElements = [];
    };
    ScrollXP.version = typeof "2.0.7" !== "undefined" ? "2.0.7" : "test";
    ScrollXP.Animation = AnimationBuilder;
    ScrollXP.Scene = SceneBuilder;
    ScrollXP.Parallax = ParallaxBuilder;
    return ScrollXP;
}());
export default ScrollXP;
//# sourceMappingURL=index.js.map